import React from 'react';
import { ArticleItemProps } from '../types/typedeclaration';
import { Link } from 'react-router-dom';
import {Loader} from './Index';
import {HEADING_STYLE_DARK_MODE, HEADING_STYLE_LIGHT_MODE} from '../Config/config';

const GalleryGrid: React.FC<ArticleItemProps> = ({
  darkMode,
  fontSize, 
  inlineView,
  articles,
  title
}) => { 
  
  const [loading, setLoading] = React.useState(false);

  // Function to render multiple images in a grid
  const renderImageGrid = (images: string[], altText: string) => {
    if (!images || images.length === 0) return null;
    
    if (images.length === 1) {
      return (
        <img 
          src={images[0]} 
          alt={altText} 
          className="w-full h-full object-cover"
        />
      );
    }

    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-1 w-full h-full">
        {images.slice(0, 4).map((img, idx) => (
          <div key={idx} className={`relative ${
            images.length === 3 && idx === 2 ? 'col-span-2' : ''
          }`}>
            <img 
              src={img} 
              alt={`${altText} ${idx + 1}`} 
              className="w-full h-full object-cover"
            />
            {idx === 3 && images.length > 4 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white text-xl font-bold">+{images.length - 4}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`${inlineView ? 'z-20' : 'fixed inset-0'} pb-16 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {loading ? (<Loader darkMode={darkMode}/>) : (
        <>
          <div className="container mx-auto px-4 pt-0"></div>
          
          <div className={`container mx-auto py-0 ${inlineView && 'px-4'}`}>
            <h2 className={`text-xl font-bold mb-6 ${darkMode ? HEADING_STYLE_DARK_MODE : HEADING_STYLE_LIGHT_MODE}`}>{title}</h2>            
            
            <div className={`grid gap-4 ${!inlineView && 'overflow-y-scroll max-h-[calc(100vh-150px)]'}`}>
              {/* Featured Article (First Item) */}
              {articles && Array.isArray(articles) && articles.length > 0 && (
                articles.slice(0,1).map((item) => {
                  
                  // Assuming item.images is an array of image URLs
                  const images = item?.extra_images || [item?.post_featured_image_thumb || item?.post_featured_image];
                  
                  return (
                    <div key={item?.id} className={`rounded-lg overflow-hidden shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="relative">
                        <Link id={item?.post_slug} to={`/photos/${item?.post_slug}`}>
                          <div className="w-full h-64">
                            {renderImageGrid(images, item?.post_title)}
                          </div>
                        </Link>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                          <div className={`text-white font-bold ${
                            fontSize === 'small' ? 'text-xl' : 
                            fontSize === 'large' ? 'text-3xl' : 'text-2xl'
                          }`}>
                            {item?.post_title}
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-gray-300">
                              <i className="far fa-clock mr-1"></i> {item?.created}
                            </span>
                            <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">
                              {item?.location}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              
              {/* Article List with alternating designs */}
              {Array.isArray(articles) && articles.length > 1 && articles.slice(1).map((item, index) => {
                // Assuming item.images is an array of image URLs
                const images = item?.extra_images || [item?.post_featured_image_thumb || item?.post_featured_image];
                
                return (
                  <div 
                    key={item.id} 
                    className={`rounded-lg overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} ${
                      index % 2 === 0 
                        ? 'shadow-md flex flex-row' 
                        : 'shadow-lg flex flex-col'
                    }`}
                  >
                    {index % 2 === 0 ? (
                      // Horizontal layout for even items
                      <>
                        <div className="w-2/5 relative">
                          <Link id={item?.post_slug} to={`/photos/${item?.post_slug}`}>
                            <div className="w-full h-full min-h-40">
                              {renderImageGrid(images, item.post_title)}
                            </div>
                          </Link>
                          <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white px-2 py-1 text-xs rounded">
                            {item.location}
                          </div>
                        </div>
                        <div className="w-3/5 p-4 flex flex-col justify-between">
                          <div>
                            <Link id={item?.post_slug} to={`/photos/${item?.post_slug}`}>
                              <h3 className={`font-bold ${
                                fontSize === 'small' ? 'text-base' : 
                                fontSize === 'large' ? 'text-xl' : 'text-lg'
                              } line-clamp-3`}>
                                {item.post_title}
                              </h3>
                            </Link>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {item.user}
                            </span>
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <i className="far fa-clock mr-1"></i> {item.created}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      // Vertical layout for odd items
                      <>
                        <div className="relative">
                          <Link id={item?.post_slug} to={`/photos/${item?.post_slug}`}>
                            <div className="w-full h-48">
                              {renderImageGrid(images, item.post_title)}
                            </div>
                          </Link>
                          <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white px-2 py-1 text-xs rounded">
                            {item.location}
                          </div>
                        </div>
                        <div className="p-4">
                          <Link id={item?.post_slug} to={`/photos/${item?.post_slug}`}>
                            <h3 className={`font-bold ${
                              fontSize === 'small' ? 'text-base' : 
                              fontSize === 'large' ? 'text-xl' : 'text-lg'
                            } line-clamp-2`}>
                              {item.post_title}
                            </h3>
                          </Link>
                          <div className="flex justify-between items-center mt-4">
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {item.user}
                            </span>
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <i className="far fa-clock mr-1"></i> {item.created}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GalleryGrid;